import { useState, useEffect, useCallback } from "react";
import { fetchFilterOptions } from "../services/trackingServiceV2";

// Filtro compartilhado pelos relatórios de Tracking (Funil, Visitantes, Leads, Vídeos).
// Período + PRODUTO (eixo do tracking novo) + filtros em cascata (página/UTMs).
// Só entram eventos com produto (snippet com METRIQA_PRODUTO); o seletor lista os
// produtos que tiveram eventos no período. A escolha fica salva no navegador.

const PRESETS = [
    { label: "Hoje", days: 0 },
    { label: "Ontem", days: -1 },
    { label: "7 dias", days: 7 },
    { label: "15 dias", days: 15 },
    { label: "30 dias", days: 30 },
];

const LS_PRODUTO = "metriqa.tracking.produto_id";

function toDateStr(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function getPresetRange(preset) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (preset.days === 0) return { start: toDateStr(today), end: toDateStr(today) };
    if (preset.days === -1) {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: toDateStr(yesterday), end: toDateStr(yesterday) };
    }
    const past = new Date(today);
    past.setDate(past.getDate() - (preset.days - 1));
    return { start: toDateStr(past), end: toDateStr(today) };
}

const selectClass = "bg-surface-3 text-white px-2 py-1.5 rounded text-xs min-w-[100px] max-w-[220px] truncate appearance-none cursor-pointer border border-white/[0.12] focus:border-emerald-500 focus:outline-none";

function extractDomain(url) {
    try {
        const u = new URL(url);
        return u.hostname + u.pathname.replace(/\/$/, "");
    } catch {
        return url;
    }
}

function lerProdutoSalvo() {
    try { return localStorage.getItem(LS_PRODUTO) || ""; } catch { return ""; }
}
function salvarProduto(v) {
    try { if (v) localStorage.setItem(LS_PRODUTO, String(v)); else localStorage.removeItem(LS_PRODUTO); } catch { /* sem storage */ }
}

const EMPTY_OPTS = { produtos: [], paginas: [], campanhas: [], mediums: [], contents: [], terms: [] };

export default function DateFilterBar({ onBuscar, loading, children, showPurchaseFilter = false }) {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [activePreset, setActivePreset] = useState(null);

    const [options, setOptions] = useState(EMPTY_OPTS);
    const [produtoId, setProdutoId] = useState(lerProdutoSalvo);
    const [pagina, setPagina] = useState("");
    const [campanha, setCampanha] = useState("");
    const [medium, setMedium] = useState("");
    const [content, setContent] = useState("");
    const [term, setTerm] = useState("");
    const [filtroCompra, setFiltroCompra] = useState("todos");
    const [loadingOpts, setLoadingOpts] = useState(false);
    const [datesLoaded, setDatesLoaded] = useState(false);

    const montarFiltros = useCallback((extra = {}) => {
        const f = {};
        if (produtoId) f.produto_id = Number(produtoId);
        if (pagina) f.pagina = pagina;
        if (campanha) f.campanha = campanha;
        if (medium) f.medium = medium;
        if (content) f.content = content;
        if (term) f.term = term;
        return { ...f, ...extra };
    }, [produtoId, pagina, campanha, medium, content, term]);

    const loadOptions = useCallback(async (sd, ed, filters = {}) => {
        if (!sd || !ed) return;
        setLoadingOpts(true);
        try {
            const opts = await fetchFilterOptions(sd, ed, filters);
            setOptions({ ...EMPTY_OPTS, ...opts });
            // produto salvo que não tem eventos no período: mantém a escolha, só avisa visualmente
        } catch {
            setOptions(EMPTY_OPTS);
        }
        setLoadingOpts(false);
    }, []);

    // Datas mudaram → recarrega opções do zero (mantém o produto)
    useEffect(() => {
        if (startDate && endDate) {
            setPagina(""); setCampanha(""); setMedium(""); setContent(""); setTerm("");
            loadOptions(startDate, endDate, produtoId ? { produto_id: Number(produtoId) } : {});
            setDatesLoaded(true);
        }
    }, [startDate, endDate, loadOptions]);

    // Produto mudou → limpa cascata e recarrega opções do produto
    useEffect(() => {
        if (!datesLoaded) return;
        setPagina(""); setCampanha(""); setMedium(""); setContent(""); setTerm("");
        loadOptions(startDate, endDate, produtoId ? { produto_id: Number(produtoId) } : {});
    }, [produtoId]);

    // Cascata: cada nível recarrega as opções com os níveis acima
    useEffect(() => {
        if (!datesLoaded) return;
        setCampanha(""); setMedium(""); setContent(""); setTerm("");
        const f = {}; if (produtoId) f.produto_id = Number(produtoId); if (pagina) f.pagina = pagina;
        loadOptions(startDate, endDate, f);
    }, [pagina]);
    useEffect(() => {
        if (!datesLoaded) return;
        setMedium(""); setContent(""); setTerm("");
        const f = {}; if (produtoId) f.produto_id = Number(produtoId); if (pagina) f.pagina = pagina; if (campanha) f.campanha = campanha;
        loadOptions(startDate, endDate, f);
    }, [campanha]);
    useEffect(() => {
        if (!datesLoaded) return;
        setContent(""); setTerm("");
        const f = {}; if (produtoId) f.produto_id = Number(produtoId); if (pagina) f.pagina = pagina; if (campanha) f.campanha = campanha; if (medium) f.medium = medium;
        loadOptions(startDate, endDate, f);
    }, [medium]);
    useEffect(() => {
        if (!datesLoaded) return;
        setTerm("");
        const f = {}; if (produtoId) f.produto_id = Number(produtoId); if (pagina) f.pagina = pagina; if (campanha) f.campanha = campanha; if (medium) f.medium = medium; if (content) f.content = content;
        loadOptions(startDate, endDate, f);
    }, [content]);

    function handleProduto(v) {
        setProdutoId(v);
        salvarProduto(v);
    }

    function handleBuscar() {
        if (!startDate || !endDate) return;
        setActivePreset(null);
        onBuscar(startDate, endDate, montarFiltros(), filtroCompra);
    }

    function handlePresetBuscar(preset) {
        const { start, end } = getPresetRange(preset);
        setStartDate(start);
        setEndDate(end);
        setActivePreset(preset.label);
        setPagina(""); setCampanha(""); setMedium(""); setContent(""); setTerm("");
        const f = produtoId ? { produto_id: Number(produtoId) } : {};
        onBuscar(start, end, f, filtroCompra);
    }

    const produtoAtual = options.produtos.find(p => String(p.id) === String(produtoId));
    const produtoSemEventos = !!produtoId && datesLoaded && !loadingOpts && !produtoAtual;

    return (
        <div className="bg-surface-2 p-4 rounded-lg space-y-3">
            {/* Linha 1: presets + datas + buscar */}
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex gap-2 mr-2">
                    {PRESETS.map(p => (
                        <button key={p.label} onClick={() => handlePresetBuscar(p)} disabled={loading}
                            className={`px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap ${activePreset === p.label ? "bg-emerald-600 text-white" : "bg-surface-3 text-ink-2 hover:bg-surface-4"} disabled:opacity-50`}>
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className="hidden sm:block w-px h-8 bg-surface-4" />
                <div>
                    <label className="block text-xs text-ink-3 mb-1">Data Início</label>
                    <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setActivePreset(null); }}
                        className="bg-surface-3 text-white px-3 py-1.5 rounded text-sm" />
                </div>
                <div>
                    <label className="block text-xs text-ink-3 mb-1">Data Fim</label>
                    <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setActivePreset(null); }}
                        className="bg-surface-3 text-white px-3 py-1.5 rounded text-sm" />
                </div>
                {children}
                <button onClick={handleBuscar} disabled={loading || !startDate || !endDate}
                    className="bg-emerald-600 hover:bg-emerald-500 px-5 py-1.5 rounded text-sm font-medium disabled:opacity-50 transition">
                    {loading ? "Buscando..." : "Buscar"}
                </button>
            </div>

            {/* Linha 2: produto + cascata */}
            {datesLoaded && (
                <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-white/[0.08]">
                    <div>
                        <label className="block text-xs text-ink-3 mb-1">Produto</label>
                        <select value={produtoId} onChange={e => handleProduto(e.target.value)} className={`${selectClass} border-emerald-500/40`}>
                            <option value="">Todos os produtos rastreados</option>
                            {options.produtos.map(p => (
                                <option key={p.id} value={p.id}>{p.nome}</option>
                            ))}
                            {produtoSemEventos && <option value={produtoId}>Produto {produtoId} (sem eventos no período)</option>}
                        </select>
                    </div>

                    {loadingOpts && (
                        <div className="flex items-center gap-1.5 text-xs text-ink-4 pb-2">
                            <div className="w-3 h-3 border border-emerald-400 border-t-transparent rounded-full animate-spin" />
                            Carregando filtros...
                        </div>
                    )}

                    {options.paginas.length > 0 && (
                        <div>
                            <label className="block text-xs text-ink-3 mb-1">Página</label>
                            <select value={pagina} onChange={e => setPagina(e.target.value)} className={selectClass}>
                                <option value="">Todas</option>
                                {options.paginas.map(p => <option key={p} value={p}>{extractDomain(p)}</option>)}
                            </select>
                        </div>
                    )}
                    {options.campanhas.length > 0 && (
                        <div>
                            <label className="block text-xs text-ink-3 mb-1">Campanha</label>
                            <select value={campanha} onChange={e => setCampanha(e.target.value)} className={selectClass}>
                                <option value="">Todas</option>
                                {options.campanhas.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    )}
                    {options.mediums.length > 0 && (
                        <div>
                            <label className="block text-xs text-ink-3 mb-1">Medium</label>
                            <select value={medium} onChange={e => setMedium(e.target.value)} className={selectClass}>
                                <option value="">Todos</option>
                                {options.mediums.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                    )}
                    {options.contents.length > 0 && (
                        <div>
                            <label className="block text-xs text-ink-3 mb-1">Content</label>
                            <select value={content} onChange={e => setContent(e.target.value)} className={selectClass}>
                                <option value="">Todos</option>
                                {options.contents.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    )}
                    {options.terms.length > 0 && (
                        <div>
                            <label className="block text-xs text-ink-3 mb-1">Term</label>
                            <select value={term} onChange={e => setTerm(e.target.value)} className={selectClass}>
                                <option value="">Todos</option>
                                {options.terms.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                    )}
                    {showPurchaseFilter && (
                        <div>
                            <label className="block text-xs text-ink-3 mb-1">Compra</label>
                            <select value={filtroCompra} onChange={e => setFiltroCompra(e.target.value)} className={selectClass}>
                                <option value="todos">Todos</option>
                                <option value="comprou">Comprou</option>
                                <option value="nao_comprou">Não comprou</option>
                            </select>
                        </div>
                    )}

                    <p className="text-[11px] text-ink-4 pb-2 basis-full">
                        Só páginas com o snippet do produto entram aqui. {produtoAtual?.primeiro_evento && <>Dados de <strong className="text-ink-3">{produtoAtual.nome}</strong> desde {new Date(produtoAtual.primeiro_evento).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}.</>}
                        {options.produtos.length === 0 && !loadingOpts && " Nenhum produto com eventos neste período."}
                    </p>
                </div>
            )}
        </div>
    );
}
